import { useCart as useCartContext } from '../context/CartContext';
import { useCallback, useMemo } from 'react';

export const useCart = () => {
  const context = useCartContext();
  
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }

  const {
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart
  } = context;

  // Memoized calculations with NaN protection
  const totalItems = useMemo(() => {
    return cartItems.reduce((sum, item) => {
      const quantity = Number(item.quantity) || 0;
      return sum + (isNaN(quantity) ? 0 : quantity);
    }, 0);
  }, [cartItems]);

  const totalPrice = useMemo(() => {
    return cartItems.reduce((sum, item) => {
      const price = Number(item.price) || 0;
      const quantity = Number(item.quantity) || 0;
      return sum + (isNaN(price * quantity) ? 0 : price * quantity);
    }, 0);
  }, [cartItems]);

  // Enhanced add to cart with strict validation
  const safeAddToCart = useCallback((product) => {
    try {
      if (!product?.id) throw new Error('Missing product ID');
      
      const price = parseFloat(product.price);
      if (isNaN(price) || price <= 0) throw new Error(`Invalid price: ${product.price}`);
      
      const quantity = Math.max(1, parseInt(product.quantity) || 1);
      
      const cartItem = {
        id: String(product.id),
        name: String(product.name || 'Unnamed Product'),
        price: price,
        image: product.image?.url || product.image || '/placeholder-product.jpg',
        category: String(product.category || 'uncategorized'),
        quantity: quantity,
        stock: Math.max(0, parseInt(product.stock) || 0)
      };

      addToCart(cartItem);
      return true;
    } catch (error) {
      console.error('Failed to add to cart:', error.message, product);
      return false;
    }
  }, [addToCart]);

  // Robust quantity update with full validation
  const safeUpdateQuantity = useCallback((id, newQuantity) => {
    try {
      if (!id) throw new Error('Missing product ID');
      
      const quantity = Math.max(1, parseInt(newQuantity) || 1);
      const item = cartItems.find(item => item.id === id);
      
      if (!item) throw new Error('Product not found in cart');
      if (item.stock && quantity > item.stock) {
        throw new Error(`Cannot exceed available stock of ${item.stock}`);
      }

      updateQuantity(id, quantity);
      return true;
    } catch (error) {
      console.error('Failed to update quantity:', error.message);
      return false;
    }
  }, [cartItems, updateQuantity]);

  // Enhanced remove with validation
  const safeRemoveFromCart = useCallback((id) => {
    if (!id || !cartItems.some(item => item.id === id)) {
      console.error('Invalid removal attempt - ID not found:', id);
      return false;
    }
    removeFromCart(id);
    return true;
  }, [cartItems, removeFromCart]);

  return {
    cartItems,
    totalItems,
    totalPrice: parseFloat(totalPrice.toFixed(2)), // Ensure 2 decimal places
    addToCart: safeAddToCart,
    removeFromCart: safeRemoveFromCart,
    updateQuantity: safeUpdateQuantity,
    clearCart,
    isInCart: (id) => cartItems.some(item => item.id === id),
    getItemQuantity: (id) => {
      const item = cartItems.find(item => item.id === id);
      return item ? Math.max(0, parseInt(item.quantity) || 0) : 0;
    },
    getItemPrice: (id) => {
      const item = cartItems.find(item => item.id === id);
      return item ? parseFloat(item.price.toFixed(2)) : 0;
    }
  };
};